import React, { useMemo, useState } from 'react';
import ImageListItem from '../../../../../components/envago/ImageListItem/ImageListItem';
import Dialog from '../../../../../components/envago/Dialog/Dialog';
import { useTranslation } from 'react-i18next';
import Carousel, { PreviewImage } from '../../../../../components/envago/Carousel/Carousel';
import Button from '../../../../../components/envago/Button/Button';
import classNames from 'classnames';

interface JobReadingImagesProps {
    taskId?: string;
    images: Array<MeterReadingPreviewImage>;
    containerClassName?: string;
    imageSize?: 'small' | 'large' | 'auto';
    previewImageClassName?: string;
    /**
     * Anzahl der laut Backend vorhandenen Bilder. Wird gesetzt, wenn bereits bekannt ist,
     * dass Bilder existieren, diese aber noch geladen werden. Für jedes noch nicht geladene
     * Bild wird ein Platzhalter in Bildgröße gerendert, damit beim Nachladen kein Layout-Shift
     * ("Zucken") entsteht.
     */
    expectedImageCount?: number;
}

export interface MeterReadingPreviewImage extends PreviewImage {
    taskId: string;
    imageId: string;
}

const JobMeterRateTaskReadingImages = ({ taskId, images, imageSize = 'large', containerClassName, previewImageClassName, expectedImageCount }: JobReadingImagesProps) => {
    const { t } = useTranslation();

    const [open, setOpen] = useState(false);
    const [initialIndex, setInitialIndex] = useState<number | undefined>(undefined);

    const taskResultImages = useMemo(() => {
        if (!taskId) {
            return images;
        }

        return images.filter((i) => i.taskId === taskId);
    }, [images, taskId]);

    // Solange die tatsächlichen Bilddaten noch geladen werden, für jedes laut Backend
    // erwartete Bild einen Platzhalter in Bildgröße rendern, damit kein Layout-Shift entsteht.
    const placeholderCount = Math.max(0, (expectedImageCount ?? 0) - taskResultImages.length);

    return (
        <>
            <div className={classNames(containerClassName ? containerClassName : 'flex flex-row gap-2')}>
                {taskResultImages.map((i, index) => {
                    return (
                        <ImageListItem
                            key={i.imageId}
                            showDetails={false}
                            image={i.base64}
                            imageSize={imageSize}
                            className={previewImageClassName}
                            onImageClick={() => {
                                setInitialIndex(!taskId ? index : i.index);
                                setOpen(true);
                            }}
                        />
                    );
                })}
                {Array.from({ length: placeholderCount }).map((_, index) => (
                    <div
                        key={`placeholder-${index}`}
                        aria-hidden={true}
                        className={classNames(previewImageClassName, 'border rounded flex items-center px-2 py-1 mt-1 bg-gray-100')}
                    >
                        <div
                            className={classNames('rounded bg-gray-200 animate-pulse', {
                                'h-8 w-8': imageSize === 'small',
                                'h-24 w-24': imageSize !== 'small',
                            })}
                        />
                    </div>
                ))}
            </div>
            <Dialog
                title={t('jobs.details.dialogs.meterRateTaskReadingImages.title')}
                classNameContent={'h-auto sm:h-full w-full sm:w-1/2'}
                visible={open}
                closable
                onClose={() => {
                    setOpen(false);
                    setTimeout(() => {
                        setInitialIndex(undefined);
                    }, 250);
                }}
                actions={[
                    <Button
                        key={'meterRateTaskReadingImages.closeButton'}
                        primary
                        onClick={() => {
                            setOpen(false);
                            setTimeout(() => {
                                setInitialIndex(undefined);
                            }, 250);
                        }}
                    >
                        {t('jobs.details.dialogs.meterRateTaskReadingImages.closeButton')}
                    </Button>,
                ]}
            >
                <Carousel
                    images={images}
                    options={{
                        startIndex: initialIndex,
                        align: 'center',
                    }}
                />
            </Dialog>
        </>
    );
};

export default JobMeterRateTaskReadingImages;
